import { useEffect, useRef, useCallback, useState } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const;
const STORAGE_KEY = 'proptrust_last_activity';
const WARNING_LEAD_MS = 2 * 60 * 1000; // 2 minutes before expiry

interface UseInactivityTimeoutOptions {
  timeoutMs: number;
  onWarning: () => void;
  onTimeout: () => void;
  enabled: boolean;
}

export function useInactivityTimeout({ timeoutMs, onWarning, onTimeout, enabled }: UseInactivityTimeoutOptions) {
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expireTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isWarning, setIsWarning] = useState(false);

  const clearTimers = useCallback(() => {
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (expireTimer.current) clearTimeout(expireTimer.current);
    warningTimer.current = null;
    expireTimer.current = null;
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    clearTimers();
    setIsWarning(false);

    // Broadcast activity to other tabs
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      return;
    }

    const warningDelay = Math.max(timeoutMs - WARNING_LEAD_MS, 0);

    warningTimer.current = setTimeout(() => {
      setIsWarning(true);
      onWarning();
    }, warningDelay);

    expireTimer.current = setTimeout(() => {
      onTimeout();
    }, timeoutMs);
  }, [enabled, timeoutMs, onWarning, onTimeout, clearTimers]);

  // Listen for cross-tab activity
  useEffect(() => {
    if (!enabled) return;

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        resetTimer();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [enabled, resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => resetTimer();

    ACTIVITY_EVENTS.forEach(event =>
      document.addEventListener(event, handleActivity, { passive: true })
    );

    // Start initial timer
    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach(event =>
        document.removeEventListener(event, handleActivity)
      );
      clearTimers();
    };
  }, [enabled, resetTimer, clearTimers]);

  return { isWarning, resetTimer, remainingMs: timeoutMs };
}
